import moment from 'moment';
import { SearchCompanyByABNResult } from '../domain/application-service.domain';
import { BusinessNumberSearchValue } from '../domain/component/business-number-search-component.domain';
import { EntityTypes, EntityTypeValueOptions } from '../domain/component/entity-type-component.domain';

/**
 * Returns the latest entry in an array (or single object) by effectiveFrom date.
 */
export const latestByEffectiveFrom = <T extends { effectiveFrom: string }>(items: T | T[] | undefined): T | undefined => {
  if (!items) return undefined;
  const arr = Array.isArray(items) ? items : [items];
  return arr.reduce<T | undefined>((best, item) => {
    if (!best) return item;
    return item.effectiveFrom > best.effectiveFrom ? item : best;
  }, undefined);
};

/**
 * Returns the latest Active entity status entry by effectiveFrom date.
 * When a business is cancelled, ABR returns both an Active and a Cancelled record.
 * Using the generic latestByEffectiveFrom would pick the Cancelled record (whose
 * effectiveFrom is the cancellation date), giving a wrong age and a false no-gap result.
 */
export const latestActiveEntityStatus = <T extends { effectiveFrom: string; entityStatusCode?: string }>(
  items: T | T[] | undefined
): T | undefined => {
  if (!items) return undefined;
  const arr = Array.isArray(items) ? items : [items];
  const activeItems = arr.filter(i => i.entityStatusCode === 'Active');
  if (activeItems.length === 0) return latestByEffectiveFrom(items);
  return activeItems.reduce<T | undefined>((best, item) => {
    if (!best) return item;
    return item.effectiveFrom > best.effectiveFrom ? item : best;
  }, undefined);
};

/**
 * Check whether two registration periods are continuous (no gap between them).
 * A period with effectiveTo '0001-01-01' is treated as still-active (no end date).
 * Consecutive periods where the previous ended the day before the next started
 * (e.g. prev ended 2024-06-30, cur started 2024-07-01) are also treated as continuous.
 */
export const hasNoContinuityGap = (
  prev: { effectiveTo: string } | undefined,
  cur: { effectiveFrom: string } | undefined
): boolean => {
  if (!prev) return false;
  if (prev.effectiveTo === '0001-01-01') return true;
  if (!cur) return false;
  if (cur.effectiveFrom <= prev.effectiveTo) return true;
  // Also treat consecutive days as continuous (prev ended the day before cur started)
  return moment(cur.effectiveFrom).diff(moment(prev.effectiveTo), 'days') === 1;
};

const ABN_WEIGHTS = [10, 1, 3, 5, 7, 9, 11, 13, 15, 17, 19];

/**
 * ABN checksum per https://abr.business.gov.au/Help/AbnFormat: subtract 1 from the first digit,
 * weight each digit, and the sum must be divisible by 89. Spaces are ignored.
 */
export const isValidAbn = (abn: string | null | undefined): boolean => {
  const digits = (abn ?? '').replace(/\s/g, '');
  if (!/^\d{11}$/.test(digits)) return false;
  const sum = digits.split('').reduce((acc, ch, idx) => {
    const digit = Number(ch) - (idx === 0 ? 1 : 0);
    return acc + digit * ABN_WEIGHTS[idx];
  }, 0);
  return sum % 89 === 0;
};

export const ageInMonths = (effectiveFrom: string | undefined): number | undefined =>
  effectiveFrom ? moment().diff(effectiveFrom, 'months') : undefined;

/** The codes CommercialEntity.EntityType / ApplicationCommercialEntity.entityType are stored as. */
export type EntityTypeCode = 'TRST' | 'PTNR' | 'PTY' | 'SLTR';

/**
 * Convert the EntityTypes form used across the UI into the stored code.
 *
 * 'other' is the ABR fallback and has no code of its own; toInteflowEntityTypes has always stored it
 * as a company, so this matches rather than introducing a fifth code the readers would not know.
 */
export const entityTypeCodeFromEntityType = (
  entityType: EntityTypes | null | undefined,
): EntityTypeCode | null => {
  switch (entityType) {
    case 'trust': return 'TRST';
    case 'partnership': return 'PTNR';
    case 'sole-trader': return 'SLTR';
    case 'company':
    case 'other': return 'PTY';
    default: return null;
  }
};

/**
 * Convert a stored entity type back into the EntityTypes form the UI and the KYB rules work in.
 *
 * Accepts the EntityTypes form as well, because rows written by the KYB path before it was aligned
 * on codes hold those values - so both vintages read back the same and no backfill is needed.
 * 'P/L' is a legacy company code still present in production data (see ppsr/consent-form).
 */
export const entityTypeFromCode = (value: string | null | undefined): EntityTypes | null => {
  switch ((value ?? '').trim().toUpperCase()) {
    case 'TRST': return 'trust';
    case 'PTNR': return 'partnership';
    case 'SLTR': return 'sole-trader';
    case 'PTY':
    case 'P/L': return 'company';
  }
  return EntityTypeValueOptions.some(option => option.type === value) ? (value as EntityTypes) : null;
};

/**
 * Derive the internal CommercialEntity.EntityType code from a business search value.
 *
 * Mirrors the frontend getEntityType()/toInteflowEntityTypes() classification so an entity
 * created server-side (e.g. a corporate guarantor added via an individual's additional role)
 * carries the same code the full submit flow would have set. Returns null when the type cannot
 * be determined, so callers can preserve an existing value rather than guess.
 */
export const entityTypeCodeFromBusiness = (
  business: BusinessNumberSearchValue,
): EntityTypeCode | null => {
  if (!business) return null;
  const result = business.result;
  if (result) {
    // A company is identified by its organisation (ACN) number.
    if (result['organisation-number']) return 'PTY';
    const report = result['australian-business-register-report'];
    if (report && report.type === 'result') {
      if (report.EntityTypeInd === 'IND') return 'SLTR';
      const text = (report.EntityTypeText ?? '').toLowerCase();
      if (text.includes('trust')) return 'TRST';
      if (text.includes('partnership')) return 'PTNR';
    }
  }
  // The full search result isn't always carried on a persisted role, but a registered company
  // always has an ACN - so an ACN alone is enough to classify it as a company.
  if (business.acn) return 'PTY';
  return null;
};

/**
 * Like ageInMonths but respects the GST registration's effectiveTo date.
 * '0001-01-01' is the ABR sentinel meaning "no end date / still active".
 * Any other non-empty effectiveTo means the registration has been cancelled,
 * so the current active GST age is 0 regardless of when it started.
 */
export const isAbrPeriodActive = (
  item: { effectiveTo?: string } | undefined
): boolean => !!item && (!item.effectiveTo || item.effectiveTo === '0001-01-01');

export const activeAgeInMonths = (
  item: { effectiveFrom: string; effectiveTo?: string } | undefined
): number | undefined => {
  if (!item) return undefined;
  if (!isAbrPeriodActive(item)) return 0;
  return ageInMonths(item.effectiveFrom);
};

/**
 * Single source of truth for all four ABN/GST age values.
 *
 * Computes base ages from the current entity's ABR data and then applies
 * business continuity by examining previousBusiness via two paths:
 *
 * Primary path: previousBusiness.abrResult carries full entityStatus[]/
 * goodsAndServicesTax[] arrays, enabling an exact hasNoContinuityGap check.
 *
 * Fallback path: when abrResult is absent (old applications or failed re-fetch),
 * falls back to the flat ABR report's ABNStatusFromDate / GSTStatusFromDate.
 * These dates only represent the CURRENT status start, so they are only used
 * when the status is still Active (otherwise the date is the cancellation date,
 * which would produce a wrong age).
 */
export const computeAges = (
  companyDetails: SearchCompanyByABNResult,
  previousBusiness?: BusinessNumberSearchValue
): {
  abnAgeMonths: number;
  gstAgeMonths: number;
  abnAgeMonthsWithContinuity: number;
  gstAgeMonthsWithContinuity: number;
} => {
  const curEntityStatus = latestByEffectiveFrom(companyDetails.entityStatus as any);
  const curGst          = latestByEffectiveFrom(companyDetails.goodsAndServicesTax as any);

  const abnAgeMonths = ageInMonths(curEntityStatus?.effectiveFrom) ?? companyDetails.ABNAgeMonths ?? 0;
  const gstAgeMonths = activeAgeInMonths(curGst) ?? companyDetails.GSTAgeMonths ?? 0;

  if (previousBusiness?.abrResult) {
    const abrResult        = previousBusiness.abrResult;
    const prevEntityStatus = latestActiveEntityStatus(abrResult.entityStatus as any);
    const prevGst          = latestByEffectiveFrom(abrResult.goodsAndServicesTax as any);
    return {
      abnAgeMonths,
      gstAgeMonths,
      abnAgeMonthsWithContinuity: hasNoContinuityGap(prevEntityStatus, curEntityStatus)
        ? (ageInMonths(prevEntityStatus?.effectiveFrom) ?? abnAgeMonths)
        : abnAgeMonths,
      // Overlapping cancelled periods (including the same ABN entered as previous)
      // must not revive GST age. Continuity only extends when at least one side
      // is still GST-registered.
      gstAgeMonthsWithContinuity: (prevGst && hasNoContinuityGap(prevGst, curGst)
        && (isAbrPeriodActive(prevGst) || isAbrPeriodActive(curGst)))
        ? (ageInMonths(prevGst.effectiveFrom) ?? gstAgeMonths)
        : gstAgeMonths,
    };
  }

  if (previousBusiness && (previousBusiness.result as any)?.['australian-business-register-report']?.type === 'result') {
    const abrReport   = (previousBusiness.result as any)['australian-business-register-report'];
    const prevAbnDate = abrReport.ABNStatus === 'Active' ? (abrReport.ABNStatusFromDate as string | undefined) : undefined;
    const prevGstDate = abrReport.GSTStatus === 'Active' ? (abrReport.GSTStatusFromDate as string | undefined) : undefined;
    const prevAbnAge  = ageInMonths(prevAbnDate) ?? abnAgeMonths;
    const prevGstAge  = ageInMonths(prevGstDate) ?? gstAgeMonths;
    return {
      abnAgeMonths,
      gstAgeMonths,
      abnAgeMonthsWithContinuity: prevAbnAge > abnAgeMonths ? prevAbnAge : abnAgeMonths,
      gstAgeMonthsWithContinuity: prevGstAge > gstAgeMonths ? prevGstAge : gstAgeMonths,
    };
  }

  return {
    abnAgeMonths,
    gstAgeMonths,
    abnAgeMonthsWithContinuity: abnAgeMonths,
    gstAgeMonthsWithContinuity: gstAgeMonths,
  };
};

/**
 * Convenience wrapper over computeAges for callers that only need the
 * continuity-adjusted ages (e.g. frontend pricing override logic).
 */
export const calculateContinuityAges = (
  companyDetails: SearchCompanyByABNResult,
  previousBusiness: BusinessNumberSearchValue
): { abnAgeWithContinuity: number; gstAgeWithContinuity: number } => {
  const { abnAgeMonthsWithContinuity, gstAgeMonthsWithContinuity } = computeAges(companyDetails, previousBusiness);
  return { abnAgeWithContinuity: abnAgeMonthsWithContinuity, gstAgeWithContinuity: gstAgeMonthsWithContinuity };
};
